// Import z PODŚCIEŻKI (nie z bare "poseidon-lite") — TS rozwiązuje wprost do poseidon1.d.ts,
// omijając mapę `exports` paczki (która nie ma warunku `types` i powoduje flaky TS2307).
import { poseidon1 } from "poseidon-lite/poseidon1";

// Tajny głos ZKP — warstwa TAJNOŚCI pod istniejącą autoryzacją (token/konto/Aura decydują KTO,
// ten moduł anonimizuje JAK). Sekret żyje TYLKO na urządzeniu wyborcy; backend dostaje sam commitment.
// Dowód Groth16/BN254 generowany lokalnie przez window.snarkjs (ładowany script-tagiem w index.html).
//
// Circuit (vote_merkle.circom, levels=10):
//   commitment = Poseidon(1)([secret])           — liść w drzewie elektoratu
//   nullifier  = Poseidon(2)([secret, epoch])     — blokuje podwójny głos (publiczny)
//   public = [nullifier, epoch, vote, root]

const ZK_WASM = "/zk/vote_merkle.wasm";
const ZK_ZKEY = "/zk/votem_final.zkey";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    snarkjs: any;
  }
}

const secretStorageKey = (votingId: string) => `zkvote.secret.${votingId}`;

// Losowy element ciała BN254 (31 bajtów < r) — bezpiecznie poniżej rzędu pola.
const randomFieldElement = (): bigint => {
  const bytes = new Uint8Array(31);
  crypto.getRandomValues(bytes);
  let hex = "0x";
  bytes.forEach(b => (hex += b.toString(16).padStart(2, "0")));
  return BigInt(hex);
};

// Pobiera lub tworzy (i utrwala lokalnie) sekret wyborcy dla danego głosowania.
// UWAGA: wyczyszczenie urządzenia = utrata sekretu = utrata możliwości głosu (jak seed phrase).
export const getOrCreateZkSecret = (votingId: string): bigint => {
  const key = secretStorageKey(votingId);
  const existing = localStorage.getItem(key);
  if (existing) return BigInt(existing);
  const secret = randomFieldElement();
  localStorage.setItem(key, secret.toString());
  return secret;
};

export const hasZkSecret = (votingId: string): boolean => !!localStorage.getItem(secretStorageKey(votingId));

// "Publiczna" połowa noty — wysyłana do enrollmentu (backend NIE pozna sekretu).
export const zkCommitment = (secret: bigint): string => poseidon1([secret]).toString();

export type ZkMaterial = {
  pathElements: string[]; // rodzeństwo na ścieżce do liścia (publiczne — same hashe)
  pathIndices: number[]; // 0/1 lewo/prawo na każdym poziomie
  root: string; // korzeń drzewa elektoratu (publiczny, on-chain)
  epoch: string; // external nullifier (na głosowanie)
};

export type ZkProofBundle = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  proof: any;
  publicSignals: string[];
};

export const isSnarkjsReady = (): boolean => typeof window !== "undefined" && !!window.snarkjs;

// Generuje dowód Groth16 LOKALNIE (na urządzeniu) wiążąc wybór `vote` z członkostwem w drzewie.
export const proveZkVote = async (
  secret: bigint,
  material: ZkMaterial,
  vote: number | string
): Promise<ZkProofBundle> => {
  if (!isSnarkjsReady()) throw new Error("snarkjs nie jest załadowany (sprawdź /zk/snarkjs.min.js)");
  const input = {
    secret: secret.toString(),
    pathElements: material.pathElements,
    pathIndices: material.pathIndices,
    epoch: material.epoch,
    vote: String(vote),
    root: material.root
  };
  const { proof, publicSignals } = await window.snarkjs.groth16.fullProve(input, ZK_WASM, ZK_ZKEY);
  return { proof, publicSignals };
};
